/**
 * Copyright 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Label, Spacer, theme, Upload } from "@nordcloud/gnui";
import { FormGroup } from "~/components/Forms";
import { isValidJSONObject } from "~/utils/json";
import { useUpdateCloudAccount } from "~/views/accounts/hooks";

export const schema = z.object({
  keyFile: z
    .string()
    .min(1, "JSON File is required")
    .refine((content) => isValidJSONObject(content), "Invalid JSON format"),
});

type FormData = z.infer<typeof schema>;

type Props = {
  onSuccess: () => void;
};

export function GoogleForm({ onSuccess }: Props) {
  const [selectedFileName, setSelectedFileName] = useState<string>("");

  const { handleSubmit, formState, reset, setValue, trigger } =
    useForm<FormData>({
      resolver: zodResolver(schema),
    });

  const [updateCloudAccount, loading] = useUpdateCloudAccount({
    onSuccess: () => {
      onSuccess();
      reset();
      setSelectedFileName("");
    },
  });

  const onSubmit = (data: FormData) => updateCloudAccount(data);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();

      const handleLoad = (event: ProgressEvent<FileReader>) => {
        try {
          const content = (event.target?.result as string)?.trim();

          if (!content) {
            setValue("keyFile", "");
            setSelectedFileName("");
            return;
          }

          if (!isValidJSONObject(content)) {
            setValue("keyFile", "");
            setSelectedFileName("");
            return;
          }

          setValue("keyFile", content);
          setSelectedFileName(file.name);
          // Trigger validation after setting the value
          trigger("keyFile");
        } catch (error) {
          setValue("keyFile", "");
          setSelectedFileName("");
        } finally {
          reader.removeEventListener("load", handleLoad);
          reader.removeEventListener("error", handleError);
        }
      };

      const handleError = () => {
        setValue("keyFile", "");
        setSelectedFileName("");
        reader.removeEventListener("load", handleLoad);
        reader.removeEventListener("error", handleError);
      };

      reader.addEventListener("load", handleLoad);
      reader.addEventListener("error", handleError);
      reader.readAsText(file);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FormGroup error={formState.errors["keyFile"] || undefined}>
        <Label name="Service Account Key File" htmlFor="keyFile" required />
        <Upload
          id="keyFile"
          placeholder={
            selectedFileName || "Upload JSON service account key file"
          }
          accept=".json"
          onChange={(e) => handleFileChange(e)}
        />
      </FormGroup>
      <Spacer height={theme.spacing.spacing02} />
      <Button
        type="submit"
        initialState={loading ? "loading" : "success"}
        disabled={!formState.isValid}
      >
        Apply
      </Button>
    </form>
  );
}
